import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Field, Input } from "../../components/ui";
import { cognitoConfig, signIn } from "../../lib/cognito";
import { messageFor } from "../../lib/cognitoErrors";
import { trackLogin } from "../../lib/analytics";
import { safeNext, signupPath, DEFAULT_NEXT } from "../../lib/nextParam";
import { store } from "../../lib/session";
import { AuthAlert, AuthCard, AuthLink, AuthNote } from "./parts";

/** Sign in. Lands wherever `?next=` points, or the dashboard. */
export function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const next = safeNext(params.get("next"));
  const verified = params.get("verified") === "1";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      store(await signIn(await cognitoConfig(), email, password));
      trackLogin();
      navigate(next, { replace: true });
    } catch (err) {
      const failure = messageFor(err);
      // An unverified account can't be fixed from this form — carry the address
      // over to the code step instead of leaving the user stuck here.
      if (failure.step === "verify") {
        const verify = new URLSearchParams({ email: email.trim() });
        if (next !== DEFAULT_NEXT) verify.set("next", next);
        navigate(`/verify?${verify.toString()}`);
        return;
      }
      setError(failure.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthCard
      title="Welcome back, coach"
      subtitle="Sign in to manage your teams and registrations."
      footer={
        <>
          New here? <AuthLink to={signupPath(next)}>Create an account</AuthLink>
        </>
      }
    >
      {verified ? <AuthNote>Your email is verified. Sign in to get started.</AuthNote> : null}
      {error ? <AuthAlert>{error}</AuthAlert> : null}

      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
        </Field>

        <Field label="Password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>

        <Button type="submit" size="lg" disabled={pending} className="w-full">
          {pending ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <div className="flex flex-wrap justify-between gap-2 text-sm">
        <AuthLink to={`/forgot-password?email=${encodeURIComponent(email.trim())}`}>
          Forgot password?
        </AuthLink>
        <Link
          to={`/verify?email=${encodeURIComponent(email.trim())}`}
          className="text-slate-500 underline-offset-2 transition-colors hover:text-slate-700 hover:underline"
        >
          Need a verification code?
        </Link>
      </div>
    </AuthCard>
  );
}
